<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import camelcaseKeys from 'camelcase-keys';
import { useAlert } from 'dashboard/composables';
import DealAPI from 'dashboard/api/deals';
import { useDealsStore } from 'dashboard/stores/deals';

import Button from 'dashboard/components-next/button/Button.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import DealCreateDialog from 'dashboard/components-next/Deals/DealCreateDialog.vue';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
  contactId: { type: Number, default: null },
});

const { t, locale } = useI18n();
const store = useStore();
const dealsStore = useDealsStore();

// Rinfresca la conversazione nello store: il badge del deal (fase) nell'header/card
// riflette subito collegamenti e cambi di fase fatti da qui.
const refreshConversation = () => {
  if (props.conversationId) {
    store.dispatch('getConversation', props.conversationId);
  }
};

// Lista locale: il kanban usa lo store e non deve essere sovrascritto dal pannello.
const deals = ref([]);
const isLoading = ref(false);
const isCreating = ref(false);
const createDialogRef = ref(null);

// Collegamento a un deal esistente (per unire più conversazioni sullo stesso deal).
const isPickerOpen = ref(false);
const availableDeals = ref([]);
const selectedDealId = ref('');

const defaultPipeline = computed(
  () =>
    dealsStore.pipelines.find(pipeline => pipeline.isDefault) ||
    dealsStore.pipelines[0]
);

const formatAmount = deal =>
  new Intl.NumberFormat(locale.value, {
    style: 'currency',
    currency: deal.currency || 'EUR',
    maximumFractionDigits: 0,
  }).format((deal.amountCents || 0) / 100);

// Le fasi selezionabili per un deal sono quelle della sua pipeline.
const stageOptions = deal => {
  const pipeline = dealsStore.pipelines.find(p => p.id === deal.pipelineId);
  return (pipeline?.stages || []).map(stage => ({
    value: stage.id,
    label: stage.name,
  }));
};

const availableOptions = computed(() =>
  availableDeals.value.map(deal => ({ value: deal.id, label: deal.name }))
);

const fetchDeals = async () => {
  isLoading.value = true;
  try {
    const {
      data: { payload },
    } = await DealAPI.get({ conversation_id: props.conversationId });
    deals.value = camelcaseKeys(payload || [], { deep: true });
  } finally {
    isLoading.value = false;
  }
};

const openCreateDialog = () => createDialogRef.value?.open(null);

const onCreateDeal = async payload => {
  if (!defaultPipeline.value) {
    useAlert(t('DEALS.CONVERSATION_PANEL.NO_PIPELINE'));
    return;
  }

  isCreating.value = true;
  try {
    await dealsStore.createDeal({
      ...payload,
      pipelineId: defaultPipeline.value.id,
      contactId: props.contactId,
      conversationId: props.conversationId,
    });
    createDialogRef.value?.onSuccess();
    await fetchDeals();
    refreshConversation();
    useAlert(t('DEALS.CREATE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(error.message || t('DEALS.CREATE.API.ERROR_MESSAGE'));
  } finally {
    isCreating.value = false;
  }
};

// Cambio fase direttamente dalla conversazione (richiesta: scegliere lo stage da qui).
const onChangeStage = async (deal, stageId) => {
  try {
    const updated = await dealsStore.changeDealStage(deal.id, stageId);
    const index = deals.value.findIndex(d => d.id === deal.id);
    if (index !== -1)
      deals.value[index] = { ...deals.value[index], ...updated };
    refreshConversation();
  } catch (error) {
    useAlert(error.message || t('DEALS.CONVERSATION_PANEL.STAGE_ERROR'));
  }
};

const openPicker = async () => {
  isPickerOpen.value = true;
  selectedDealId.value = '';
  if (!defaultPipeline.value) return;
  try {
    const {
      data: { payload },
    } = await DealAPI.get({ pipeline_id: defaultPipeline.value.id });
    const all = camelcaseKeys(payload || [], { deep: true });
    const linkedIds = new Set(deals.value.map(deal => deal.id));
    availableDeals.value = all.filter(deal => !linkedIds.has(deal.id));
  } catch (error) {
    availableDeals.value = [];
  }
};

const linkExisting = async () => {
  if (!selectedDealId.value) return;
  try {
    await dealsStore.linkConversation(
      Number(selectedDealId.value),
      props.conversationId
    );
    isPickerOpen.value = false;
    selectedDealId.value = '';
    await fetchDeals();
    refreshConversation();
    useAlert(t('DEALS.CONVERSATION_PANEL.LINK_SUCCESS'));
  } catch (error) {
    useAlert(error.message || t('DEALS.CONVERSATION_PANEL.LINK_ERROR'));
  }
};

onMounted(async () => {
  if (!dealsStore.pipelines.length) await dealsStore.fetchPipelines();
  fetchDeals();
});

watch(() => props.conversationId, fetchDeals);
</script>

<template>
  <div class="flex flex-col gap-2">
    <div v-if="isLoading" class="flex justify-center py-2">
      <Spinner :size="16" />
    </div>

    <p v-else-if="!deals.length" class="text-sm text-n-slate-11">
      {{ t('DEALS.CONVERSATION_PANEL.EMPTY') }}
    </p>

    <template v-else>
      <div
        v-for="deal in deals"
        :key="deal.id"
        class="flex flex-col gap-2 px-3 py-2 rounded-lg bg-n-alpha-1"
      >
        <div class="flex items-center justify-between gap-2">
          <span class="text-sm font-medium truncate text-n-slate-12">
            {{ deal.name }}
          </span>
          <span class="text-sm font-semibold shrink-0 text-n-slate-11">
            {{ formatAmount(deal) }}
          </span>
        </div>
        <Select
          :model-value="deal.pipelineStageId"
          :options="stageOptions(deal)"
          class="!w-full"
          @update:model-value="value => onChangeStage(deal, value)"
        />
      </div>
    </template>

    <Button
      :label="t('DEALS.CONVERSATION_PANEL.CREATE')"
      variant="ghost"
      size="sm"
      icon="i-lucide-plus"
      class="justify-start"
      @click="openCreateDialog"
    />

    <div v-if="isPickerOpen" class="flex items-center gap-2">
      <Select
        :model-value="selectedDealId"
        :options="availableOptions"
        :placeholder="t('DEALS.CONVERSATION_PANEL.PICK_DEAL')"
        class="!w-full grow"
        @update:model-value="value => (selectedDealId = value)"
      />
      <Button
        icon="i-lucide-link"
        size="sm"
        :disabled="!selectedDealId"
        @click="linkExisting"
      />
    </div>
    <Button
      v-else
      :label="t('DEALS.CONVERSATION_PANEL.LINK_EXISTING')"
      variant="ghost"
      size="sm"
      icon="i-lucide-link"
      class="justify-start"
      @click="openPicker"
    />

    <DealCreateDialog
      ref="createDialogRef"
      :is-loading="isCreating"
      @create="onCreateDeal"
    />
  </div>
</template>
